import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authContext";
import { makeRequest } from "../../../utils/axios.js"; 
import { useState } from "react";
import { ethers } from "ethers";

export const usePostDetailLogic = (postId) => {
    const { currentUser } = useContext(AuthContext);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [isTipping, setIsTipping] = useState(false);

    const { 
        isLoading: postLoading, 
        error: postError, 
        data: postData 
    } = useQuery({
        queryKey: ["post", postId],
        queryFn: () => makeRequest.get("/posts/" + postId).then((res) => res.data),
    });

    const { 
        isLoading: likesLoading, 
        data: likesData 
    } = useQuery({
        queryKey: ["likes", postId],
        queryFn: () => makeRequest.get("/likes?postId=" + postId).then((res) => res.data),
    });

    const likeMutation = useMutation({
        mutationFn: (liked) => {
            if (liked) return makeRequest.delete("/likes?postId=" + postId);
            return makeRequest.post("/likes", { postId: postId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["likes"] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => makeRequest.delete("/posts/" + id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["posts"] });
            navigate("/");
        },
    });

    const { data: commentsData } = useQuery({
        queryKey: ["comments", postId],
        queryFn: () => makeRequest.get("/comments?postId=" + postId).then((res) => res.data),
    });

    const safeLikes = Array.isArray(likesData) ? likesData : [];
    const isLiked = safeLikes.some(id => String(id) === String(currentUser.id));

    const handleLike = () => {
        likeMutation.mutate(isLiked);
    };

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this post?")) {
            deleteMutation.mutate(postId);
        }
    };

    //Test
    const handleTip = async () => {
        if (postId.userId === currentUser.id) {
            return alert("You cannot tip your own post!");
        }
            
        if (!postId.walletAddress) {
            return alert("This author hasn't linked a Web3 wallet yet!");
        }
    
        if (!window.ethereum) {
            return alert("Please install MetaMask to use this feature!");
        }
    
        try {
            setIsTipping(true);
                
            const provider = new ethers.BrowserProvider(window.ethereum);
            await provider.send("eth_requestAccounts", []);
            const signer = await provider.getSigner();
    
            const tipAmount = "0.001"; 
            const tx = await signer.sendTransaction({
                to: postId.walletAddress,
                value: ethers.parseEther(tipAmount)
            });
    
            alert(`Transaction sent! Waiting for confirmation... (Hash: ${tx.hash.substring(0, 10)}...)`);
            await tx.wait(); 
                
            alert("Tip successful! Thank you for supporting the author.");
        } catch (error) {
            console.error("Tipping error:", error);
            if (error.code === 4001) {
                alert("You rejected the transaction in MetaMask.");
            } else {
                alert("Transaction failed. Please check your balance or network.");
            }
        } finally {
            setIsTipping(false);
        }
    };

    const getBadgeStyle = (type) => {
        switch (type) {
            case "Question":
                return { backgroundColor: "#ffe0b2", color: "#e65100" };
            case "Proof attempt":
                return { backgroundColor: "#e3f2fd", color: "#0d47a1" };
            case "Counterexample":
                return { backgroundColor: "#f3e5f5", color: "#4a148c" };
            case "Research idea":
                return { backgroundColor: "#e8f5e9", color: "#1b5e20" };
            case "Discussion":
            default:
                return { backgroundColor: "#f5f5f5", color: "#424242" };
        }
    };

    return {
        postData,
        postLoading,
        postError,
        likesCount: safeLikes.length,
        commentCount: commentsData?.length,
        isLiked,
        likesLoading,
        currentUser,
        isTipping, //Test
        handleLike,
        handleDelete,
        getBadgeStyle,
        handleTip //Test
    };
};